import { TeslaEntry } from 'api3';
import { CommonModule, DOCUMENT, Location } from '@angular/common';
import { Component, OnDestroy, OnInit, Renderer2, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ImageZoomDirective } from '@app/components/zoom/image-zoom-directive/image-zoom.directive';
import { environment } from '@env';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-tesla-renderer',
  templateUrl: './tesla-renderer.component.html',
  imports: [CommonModule, TranslocoPipe, ImageZoomDirective],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class TeslaRendererComponent implements OnInit, OnDestroy {
  data = input<TeslaEntry | undefined>();

  private sanitizer = inject(DomSanitizer);
  private location = inject(Location);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private route = inject(ActivatedRoute);

  private teslaCssLinks: HTMLLinkElement[] = [];
  private chatId: string | null = null;

  ngOnInit() {
    this.loadTeslaCss();
    this.extractChatId();
  }

  private extractChatId() {
    // Try to get chatId from current route or parent routes
    let route = this.route;
    while (route) {
      if (route.snapshot.paramMap.has('tabId')) {
        this.chatId = route.snapshot.paramMap.get('tabId');
        break;
      }
      route = route.parent!;
    }

    // Fallback: extract from current URL
    if (!this.chatId) {
      const urlMatch = this.location.path().match(/\/chat\/([^?\/]+)/);
      if (urlMatch) {
        this.chatId = urlMatch[1];
      }
    }
  }

  ngOnDestroy() {
    this.removeTeslaCss();
  }

  private loadTeslaCss() {
    const cssFiles = ['assets/tesla/index.css', 'assets/tesla/custom.css'];

    cssFiles.forEach((href) => {
      const link = this.renderer.createElement('link') as HTMLLinkElement;
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-tesla-styles', 'true');
      this.renderer.appendChild(this.document.head, link);
      this.teslaCssLinks.push(link);
    });
  }

  private removeTeslaCss() {
    this.teslaCssLinks.forEach((link) => {
      this.renderer.removeChild(this.document.head, link);
    });
    this.teslaCssLinks = [];
  }

  htmlData = computed(() => {
    const document = this.data();
    if (!document?.content) {
      return undefined;
    }

    // Process HTML to rewrite URLs
    const processedHtml = this.processHtmlContent(document.content);
    return this.sanitizer.bypassSecurityTrustHtml(processedHtml);
  });

  private processHtmlContent(html: string): string {
    let processed = html;

    // Rewrite Tesla image URLs to use backend proxy
    if (environment.api.url) {
      processed = processed.replace(/src="(GUID-[^"]+\.(jpg|png|svg|gif|jpeg))"/gi, `src="${environment.api.url}/rmi/tesla/proxy-image?imageId=$1"`);
    }

    // Rewrite internal document links
    if (this.chatId) {
      // Pattern: href="GUID-*.html" or href="http://*/GUID-*.html"
      processed = processed.replace(/href="(?:https?:\/\/[^"]*\/)?(GUID-[^"]+)\.html"/gi, (match, guid) => {
        const baseUrl = window.location.origin;
        return `href="${baseUrl}/chat/${this.chatId}?type=tesla&guid=${guid}&provider=tesla&articleView=true"`;
      });
    }

    return processed;
  }

  handleClick(event: Event) {
    const target = event.target as HTMLElement;
    const isInternalLink = target.tagName === 'A' && target.getAttribute('href')?.startsWith('#');

    if (isInternalLink) {
      event.preventDefault();
      const hash = target.getAttribute('href');
      if (!hash) return;

      this.scrollToInternalLink(hash);
    }
  }

  private scrollToInternalLink(hash: string) {
    const currentUrl = this.location.path().split('#')[0];
    this.location.go(currentUrl + hash);

    const elementId = hash.substring(1);
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });

      if (element.tagName === 'IMG') {
        this.highlight(element, 'border');
      } else {
        this.highlight(element, 'background');
      }
    }
  }

  private highlight(element: HTMLElement, type: 'border' | 'background' = 'border', duration = 2000) {
    const highlightClass = type === 'border' ? 'highlight-border' : 'highlight-background';

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    element.classList.add(highlightClass);

    setTimeout(() => {
      element.classList.remove(highlightClass);
    }, duration);
  }
}
